import React, { useState, lazy, Suspense } from "react";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import arabic from "react-date-object/calendars/arabic";
import arabic_ar from "react-date-object/locales/arabic_en";
import SharedHelmet from "../Helmet";
import moment from "moment-hijri";
const MainCalculatorsList = React.lazy(() => import("./MainCalculators"));
const RelatedLinks = React.lazy(() => import("../RelatedLinks"));
const DatePicker = lazy(() => import("react-multi-date-picker"));
const TITLE = "حاسبة العمر - احسب عمرك بالهجري والميلادي بدقة";
const DESCRIPTION =
  "احسب عمرك بدقة بالتقويم الهجري أو الميلادي. حاسبة عمر إلكترونية دقيقة تعرض عمرك بالسنوات والأشهر والأيام والساعات، مع معلومات إضافية عن يوم ميلادك والأيام المتبقية لعيد ميلادك القادم";
const KEYWORDS =
  "حساب العمر, احسب عمرك, حاسبة العمر, كم عمري, العمر بالهجري, العمر بالميلادي, تحويل العمر, حساب العمر بالأيام, حساب العمر بالساعات, يوم الميلاد, عيد الميلاد, حساب عمر الطفل";
const OG_URL = "https://do-calculate.com/calculator/ar/age/";
// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة العمر",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "جميع الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة العمر",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كيف أحسب عمري بدقة؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "لحساب عمرك بدقة، أدخل تاريخ ميلادك في الحاسبة سواء بالتقويم الميلادي أو الهجري. ستقوم الحاسبة بحساب عمرك بالسنوات والأشهر والأيام والساعات بدقة تامة.",
        },
      },
      {
        "@type": "Question",
        name: "ما الفرق بين حساب العمر بالهجري والميلادي؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "التقويم الهجري قمري ويتكون من 354 أو 355 يوماً، بينما التقويم الميلادي شمسي ويتكون من 365 أو 366 يوماً. الفرق يزداد بمعدل سنة واحدة تقريباً كل 33 سنة ميلادية.",
        },
      },
    ],
  },
];
const DAYS_OF_WEEK = [
  "الأحد",
  "الاثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
  "الجمعة",
  "السبت",
];
const SEASONS = [
  {
    name: "الشتاء",
    start: { month: 12, day: 21 },
    end: { month: 3, day: 20 },
  },
  { name: "الربيع", start: { month: 3, day: 21 }, end: { month: 6, day: 20 } },
  { name: "الصيف", start: { month: 6, day: 21 }, end: { month: 9, day: 22 } },
  {
    name: "الخريف",
    start: { month: 9, day: 23 },
    end: { month: 12, day: 20 },
  },
];
function AgeCalculatorGregorian() {
  const [convertedValue, setConvertedValue] = useState("gregorian");
  const [dateOfBirthGregorian, setDateOfBirthGregorian] = useState("");
  const [valueHijri, setValueHijri] = useState("");
  const [ageResult, setAgeResult] = useState(null);
  const [error, setError] = useState(null);
  const handleConversionValueChange = (newConversionType) => {
    setConvertedValue(newConversionType);
    setError(null);
    setDateOfBirthGregorian("");
    setAgeResult(null);
    setValueHijri("");
  };
  const handleDateChangeGregorian = (event) => {
    setDateOfBirthGregorian(event.target.value);
  };
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime()) || birth > today) {
      setError("يُرجى إدخال تاريخ ميلاد صحيح");
      setAgeResult(null);
      return;
    }
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    const hours = today.getHours() - birth.getHours();
    const minutes = today.getMinutes() - birth.getMinutes();
    const seconds = today.getSeconds() - birth.getSeconds();
    // Calculate total days lived
    const totalDays = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    const totalHours = Math.floor((today - birth) / (1000 * 60 * 60));
    const totalMinutes = Math.floor((today - birth) / (1000 * 60));
    const totalSeconds = Math.floor((today - birth) / 1000);
    // Calculate next birthday
    let nextBirthday = new Date(
      today.getFullYear(),
      birth.getMonth(),
      birth.getDate()
    );
    if (nextBirthday < today) {
      nextBirthday.setFullYear(today.getFullYear() + 1);
    }
    const daysUntilBirthday = Math.ceil(
      (nextBirthday - today) / (1000 * 60 * 60 * 24)
    );
    // Get day of week born
    const dayOfWeekBorn = DAYS_OF_WEEK[birth.getDay()];
    // Get season born
    const birthMonth = birth.getMonth() + 1;
    const birthDay = birth.getDate();
    let seasonBorn = "";
    for (const season of SEASONS) {
      const inStartRange =
        birthMonth > season.start.month ||
        (birthMonth === season.start.month && birthDay >= season.start.day);
      const inEndRange =
        birthMonth < season.end.month ||
        (birthMonth === season.end.month && birthDay <= season.end.day);
      if (season.name === "الشتاء") {
        if (birthMonth === 12 || birthMonth <= 3) {
          if (
            (birthMonth === 12 && birthDay >= 21) ||
            birthMonth < 3 ||
            (birthMonth === 3 && birthDay <= 20)
          ) {
            seasonBorn = season.name;
            break;
          }
        }
      } else if (inStartRange && inEndRange) {
        seasonBorn = season.name;
        break;
      }
    }
    // Life milestones
    const ageInYears = years;
    let lifeMilestone = "";
    if (ageInYears < 1) lifeMilestone = "رضيع";
    else if (ageInYears < 3) lifeMilestone = "طفل صغير";
    else if (ageInYears < 13) lifeMilestone = "طفل";
    else if (ageInYears < 20) lifeMilestone = "مراهق";
    else if (ageInYears < 40) lifeMilestone = "شاب";
    else if (ageInYears < 60) lifeMilestone = "منتصف العمر";
    else lifeMilestone = "كبير السن";
    setAgeResult({
      years,
      months,
      days,
      hours: Math.abs(hours),
      minutes: Math.abs(minutes),
      seconds: Math.abs(seconds),
      totalDays,
      totalHours,
      totalMinutes,
      totalSeconds,
      daysUntilBirthday,
      dayOfWeekBorn,
      seasonBorn,
      lifeMilestone,
      heartbeats: Math.floor(totalDays * 100000), // ~100,000 beats per day
      breaths: Math.floor(totalDays * 20000), // ~20,000 breaths per day
      sleepHours: Math.floor(totalHours / 3), // ~8 hours per day
    });
    setError(null);
  };
  const handleCalculate = () => {
    if (convertedValue === "gregorian") {
      if (!dateOfBirthGregorian) {
        setError("يُرجى إدخال تاريخ الميلاد");
        return;
      }
      calculateAge(dateOfBirthGregorian);
    } else {
      if (!valueHijri) {
        setError("يُرجى إدخال تاريخ الميلاد");
        return;
      }
      try {
        // Convert Hijri to Gregorian using moment-hijri
        // valueHijri is a DateObject from react-multi-date-picker
        const hijriYear = valueHijri.year;
        const hijriMonth = valueHijri.month.number; // 1-12
        const hijriDay = valueHijri.day;
        // Create moment-hijri date and convert to Gregorian
        const hijriMoment = moment(`${hijriYear}/${hijriMonth}/${hijriDay}`, 'iYYYY/iM/iD');
        const gregorianDate = hijriMoment.format('YYYY-MM-DD');
        calculateAge(gregorianDate);
      } catch (err) {
        console.error("Error converting Hijri date:", err);
        setError("حدث خطأ في تحويل التاريخ الهجري. يُرجى التحقق من التاريخ المدخل.");
        setAgeResult(null);
      }
    }
  };
  return (
    <div style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "24px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Quick Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة العمر الإلكترونية</strong> توفر لك طريقة سريعة ودقيقة
                لحساب عمرك بالتقويم الميلادي أو الهجري. احصل على معلومات تفصيلية
                عن عمرك بالسنوات والأشهر والأيام والساعات، بالإضافة إلى معلومات
                شيقة مثل يوم ميلادك والأيام المتبقية لعيد ميلادك القادم.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-3">
              <div className="p-3 mb-3 bg-success text-white rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  احسب عمرك الآن
                </h2>
              </div>
              <div className="mb-3">
                <label >
                  <strong>اختر نوع التقويم:</strong>
                </label>
                <div style={{ marginRight: "20px" }}>
                  <label style={{ marginLeft: "10px" }}>
                    <input
                      type="radio"
                      name="calendarType"
                      value="gregorian"
                      checked={convertedValue === "gregorian"}
                      onChange={() => handleConversionValueChange("gregorian")}
                      style={{ marginLeft: "5px" }}
                    />
                    ميلادي
                  </label>
                  <label style={{ marginLeft: "10px" }}>
                    <input
                      type="radio"
                      name="calendarType"
                      value="hijri"
                      checked={convertedValue === "hijri"}
                      onChange={() => handleConversionValueChange("hijri")}
                      style={{ marginLeft: "5px" }}
                    />
                    هجري
                  </label>
                </div>
              </div>
              {convertedValue === "gregorian" ? (
                <div className="row">
                  <div className="col-md-12">
                    <label
                      htmlFor="dateOfBirth"
                      
                      style={{ fontSize: "16px" }}
                    >
                      أدخل تاريخ ميلادك بالتقويم الميلادي:
                    </label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      value={dateOfBirthGregorian}
                      onChange={handleDateChangeGregorian}
                      className="form-control"
                      style={{ fontSize: "16px", padding: "10px" }}
                    />
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-12">
                    <label
                      htmlFor="hijriDatePicker"
                      
                      style={{ fontSize: "16px" }}
                    >
                      أدخل تاريخ ميلادك بالتقويم الهجري:
                    </label>
                    <Suspense fallback={<div>جاري التحميل...</div>}>
                      <DatePicker
                        value={valueHijri}
                        id="hijriDatePicker"
                        calendar={arabic}
                        locale={arabic_ar}
                        onChange={setValueHijri}
                        style={{ padding: "10px", width: "100%" }}
                        containerStyle={{ width: "100%" }}
                      />
                    </Suspense>
                  </div>
                </div>
              )}
              <br />
              <div className="text-center mb-3 mt-4">
                <button
                  className="btn btn-success"
                  onClick={handleCalculate}
                  style={{ fontSize: "18px", padding: "10px 40px" }}
                >
                  احسب عمري
                </button>
              </div>
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}
              {ageResult && (
                <div
                  className="alert alert-success"
                  role="alert"
                  style={{ fontSize: "16px" }}
                >
                  <h3 style={{ fontSize: "20px", marginBottom: "15px" }}>
                    <strong>نتيجة حساب العمر:</strong>
                  </h3>
                  <div className="mb-3">
                    <strong>عمرك هو:</strong> {ageResult.years} سنة و{" "}
                    {ageResult.months} شهر و {ageResult.days} يوم
                  </div>
                  <div className="mb-3">
                    <strong>عمرك بالتفصيل:</strong>
                    <ul className="mt-2">
                      <li>السنوات: {ageResult.years}</li>
                      <li>الأشهر: {ageResult.months}</li>
                      <li>الأيام: {ageResult.days}</li>
                      <li>الساعات: {ageResult.hours}</li>
                      <li>الدقائق: {ageResult.minutes}</li>
                      <li>الثواني: {ageResult.seconds}</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <strong>إحصائيات إضافية:</strong>
                    <ul className="mt-2">
                      <li>إجمالي الأيام المعاشة: {ageResult.totalDays} يوم</li>
                      <li>إجمالي الساعات: {ageResult.totalHours} ساعة</li>
                      <li>إجمالي الدقائق: {ageResult.totalMinutes} دقيقة</li>
                      <li>إجمالي الثواني: {ageResult.totalSeconds} ثانية</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <strong>معلومات شيقة:</strong>
                    <ul className="mt-2">
                      <li>ولدت يوم: {ageResult.dayOfWeekBorn}</li>
                      <li>في فصل: {ageResult.seasonBorn}</li>
                      <li>
                        الأيام المتبقية لعيد ميلادك القادم:{" "}
                        {ageResult.daysUntilBirthday} يوم
                      </li>
                      <li>مرحلة الحياة: {ageResult.lifeMilestone}</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <strong>حقائق حصلت في حياتك (تقريباً):</strong>
                    <ul className="mt-2">
                      <li>
                        نبض قلبك حوالي: {ageResult.heartbeats.toLocaleString()}{" "}
                        مرة
                      </li>
                      <li>
                        تنفست حوالي: {ageResult.breaths.toLocaleString()} مرة
                      </li>
                      <li>
                        أمضيت نائماً حوالي: {ageResult.sleepHours.toLocaleString()}{" "}
                        ساعة
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Informational Accordion */}
            <div className="info-sections-container">
              <div className="info-section">
                <div className="info-section-header">
                  <strong>ما هي حاسبة العمر؟</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    حاسبة العمر هي أداة إلكترونية متطورة تساعدك على حساب عمرك بدقة
                    عالية بناءً على تاريخ ميلادك. توفر الحاسبة معلومات شاملة عن
                    عمرك بالسنوات والأشهر والأيام والساعات والدقائق والثواني.
                  </p>
                  <p>
                    من الصعب على الإنسان حساب عمره بدقة دون الاستعانة ببرامج
                    الحاسوب الذكية، حيث تتطلب العملية الحسابية الأخذ بعين الاعتبار
                    السنوات الكبيسة والأشهر المختلفة الأطوال. لذلك توفر حاسبة العمر
                    الإلكترونية حلاً سريعاً ودقيقاً لهذه المشكلة.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>كيفية حساب العمر؟</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    حساب العمر يعتمد على فكرة الطرح بين تاريخين: تاريخ اليوم وتاريخ
                    الميلاد. لكن العملية أكثر تعقيداً نظراً لوجود ثلاثة متغيرات:
                    السنة والشهر واليوم.
                  </p>
                  <h4>خطوات حساب العمر:</h4>
                  <ol>
                    <li>
                      <strong>حساب الأيام:</strong> اطرح يوم الميلاد من يوم اليوم.
                      إذا كانت النتيجة سالبة، أضف عدد أيام الشهر السابق وانقص شهراً
                      واحداً من الأشهر.
                    </li>
                    <li>
                      <strong>حساب الأشهر:</strong> اطرح شهر الميلاد من شهر اليوم.
                      إذا كانت النتيجة سالبة، أضف 12 شهراً وانقص سنة واحدة من
                      السنوات.
                    </li>
                    <li>
                      <strong>حساب السنوات:</strong> اطرح سنة الميلاد من سنة اليوم.
                    </li>
                  </ol>
                  <p>
                    تقوم حاسبتنا الإلكترونية بهذه العمليات الحسابية تلقائياً في أقل
                    من ثانية، مع الأخذ بعين الاعتبار السنوات الكبيسة والأشهر ذات
                    الأطوال المختلفة.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>الفرق بين التقويم الميلادي والهجري</strong>
                </div>
                <div className="info-section-body">
                  <h4>التقويم الميلادي (الشمسي):</h4>
                  <ul>
                    <li>يعتمد على دورة الشمس حول الأرض</li>
                    <li>السنة العادية 365 يوماً والكبيسة 366 يوماً</li>
                    <li>يستخدم في معظم دول العالم</li>
                    <li>الفصول الأربعة ثابتة في نفس الأشهر كل عام</li>
                  </ul>
                  <h4>التقويم الهجري (القمري):</h4>
                  <ul>
                    <li>يعتمد على دورة القمر حول الأرض</li>
                    <li>السنة 354 أو 355 يوماً</li>
                    <li>يستخدم في الدول الإسلامية للمناسبات الدينية</li>
                    <li>الأشهر تتحرك عبر الفصول على مر السنين</li>
                  </ul>
                  <h4>الفرق بين التقويمين:</h4>
                  <p>
                    الفرق بين التقويم الهجري والميلادي يزداد بمعدل سنة واحدة تقريباً
                    كل 33 سنة ميلادية. بمعنى أن من عمره 33 سنة ميلادياً يكون عمره
                    34 سنة هجرياً، وهذا الفرق يزداد مع التقدم في العمر.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>حساب العمر بالأيام والساعات</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    توفر حاسبة العمر معلومات تفصيلية عن عمرك بوحدات زمنية مختلفة:
                  </p>
                  <ul>
                    <li>
                      <strong>بالسنوات:</strong> العمر الكامل بالسنوات
                    </li>
                    <li>
                      <strong>بالأشهر:</strong> إجمالي الأشهر التي عشتها
                    </li>
                    <li>
                      <strong>بالأيام:</strong> العدد الإجمالي للأيام منذ الولادة
                    </li>
                    <li>
                      <strong>بالساعات:</strong> إجمالي ساعات حياتك
                    </li>
                    <li>
                      <strong>بالدقائق والثواني:</strong> لمعرفة عمرك بدقة متناهية
                    </li>
                  </ul>
                  <p>
                    حساب العمر بالأيام مفيد بشكل خاص لأنه لا يتأثر بالفرق بين
                    التقويمين الهجري والميلادي - فاليوم هو يوم في أي تقويم. هذا
                    يجعله الطريقة الأكثر دقة لقياس العمر عند المقارنة بين
                    التقويمين.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>معلومات إضافية عن يوم ميلادك</strong>
                </div>
                <div className="info-section-body">
                  <h4>يوم الأسبوع الذي ولدت فيه:</h4>
                  <p>
                    الكثير من الناس لا يعرفون اليوم الذي ولدوا فيه من أيام الأسبوع.
                    حاسبة العمر تخبرك بدقة إن كنت قد ولدت يوم الأحد، الاثنين،
                    الثلاثاء، الأربعاء، الخميس، الجمعة، أو السبت.
                  </p>
                  <h4>الفصل الذي ولدت فيه:</h4>
                  <p>تحدد الحاسبة الفصل الذي ولدت فيه من بين الفصول الأربعة:</p>
                  <ul>
                    <li>فصل الشتاء: من 21 ديسمبر إلى 20 مارس</li>
                    <li>فصل الربيع: من 21 مارس إلى 20 يونيو</li>
                    <li>فصل الصيف: من 21 يونيو إلى 22 سبتمبر</li>
                    <li>فصل الخريف: من 23 سبتمبر إلى 20 ديسمبر</li>
                  </ul>
                  <h4>المتبقي لعيد ميلادك:</h4>
                  <p>
                    تحسب الحاسبة تلقائياً عدد الأيام المتبقية لعيد ميلادك القادم،
                    مما يساعدك على التخطيط للاحتفال به مع الأهل والأصدقاء.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>حساب عمر الطفل</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    حساب عمر الطفل بدقة مهم جداً للآباء والأمهات، حيث يساعد في:
                  </p>
                  <ul>
                    <li>متابعة النمو والتطور بشكل صحيح</li>
                    <li>تحديد التطعيمات المناسبة في الوقت المناسب</li>
                    <li>معرفة المراحل الغذائية المناسبة</li>
                    <li>تتبع المعالم التطورية (المشي، الكلام، إلخ)</li>
                    <li>تسجيل الطفل في المدرسة أو الروضة</li>
                  </ul>
                  <p>
                    حاسبتنا دقيقة جداً حتى للأطفال حديثي الولادة - يمكنك حساب عمر
                    طفلك بالأيام والساعات منذ لحظة ولادته.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>حقائق مثيرة عن عمرك</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    بناءً على عدد الأيام التي عشتها، تقدم الحاسبة إحصائيات مثيرة
                    للاهتمام:
                  </p>
                  <ul>
                    <li>
                      <strong>نبضات القلب:</strong> القلب ينبض حوالي 100,000 مرة في
                      اليوم، أي حوالي 70 نبضة في الدقيقة
                    </li>
                    <li>
                      <strong>الأنفاس:</strong> نتنفس حوالي 20,000 نفس في اليوم،
                      أي حوالي 14-16 نفساً في الدقيقة
                    </li>
                    <li>
                      <strong>ساعات النوم:</strong> نقضي حوالي ثلث حياتنا نائمين
                      (بمعدل 8 ساعات يومياً)
                    </li>
                  </ul>
                  <p>
                    هذه الأرقام تقريبية وتعتمد على المعدلات الصحية الطبيعية للإنسان
                    السليم.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>تحويل العمر بين التقويمين</strong>
                </div>
                <div className="info-section-body">
                  <p>
                    حاسبة العمر توفر إمكانية تحويل العمر من التقويم الميلادي إلى
                    الهجري والعكس. هذه الخاصية مفيدة جداً لـ:
                  </p>
                  <ul>
                    <li>
                      المقيمين خارج الدول العربية الذين يريدون معرفة عمرهم بالهجري
                    </li>
                    <li>معرفة تاريخ الميلاد في التقويم الآخر</li>
                    <li>تسهيل التعاملات الرسمية في الدول المختلفة</li>
                    <li>التخطيط للمناسبات الدينية</li>
                  </ul>
                  <p>
                    تعتمد عملية التحويل على وظائف برمجية دقيقة تأخذ في الاعتبار
                    الفروقات بين التقويمين وطبيعة كل منهما.
                  </p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>لماذا استخدام حاسبة العمر الإلكترونية؟</strong>
                </div>
                <div className="info-section-body">
                  <h4>مزايا استخدام الحاسبة:</h4>
                  <ul>
                    <li>
                      <strong>السرعة:</strong> نتائج فورية في أقل من ثانية
                    </li>
                    <li>
                      <strong>الدقة:</strong> حسابات دقيقة 100% مع مراعاة السنوات
                      الكبيسة
                    </li>
                    <li>
                      <strong>السهولة:</strong> واجهة بسيطة وسهلة الاستخدام
                    </li>
                    <li>
                      <strong>الشمولية:</strong> معلومات تفصيلية وإحصائيات إضافية
                    </li>
                    <li>
                      <strong>المجانية:</strong> خدمة مجانية 100% بدون أي رسوم
                    </li>
                  </ul>
                  <h4>استخدامات حاسبة العمر:</h4>
                  <ul>
                    <li>معرفة العمر الدقيق للتسجيل في الخدمات المختلفة</li>
                    <li>حساب عمر الأطفال لمتابعة نموهم</li>
                    <li>التخطيط للتقاعد ومعرفة السنوات المتبقية</li>
                    <li>حساب الأعمار للمناسبات والاحتفالات</li>
                    <li>معرفة الفارق العمري بين شخصين</li>
                  </ul>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>أسئلة شائعة</strong>
                </div>
                <div className="info-section-body">
                  <h4>هل الحاسبة دقيقة؟</h4>
                  <p>
                    نعم، الحاسبة دقيقة جداً وتأخذ في الاعتبار جميع العوامل مثل
                    السنوات الكبيسة والأشهر ذات الأطوال المختلفة.
                  </p>
                  <h4>هل يمكن حساب عمر طفل حديث الولادة؟</h4>
                  <p>
                    نعم، يمكنك حساب عمر الطفل من يوم ولادته بدقة بالأيام والساعات.
                  </p>
                  <h4>ما الفرق بين حساب العمر بالهجري والميلادي؟</h4>
                  <p>
                    السنة الهجرية أقصر من الميلادية بحوالي 11 يوماً، لذلك يزداد
                    الفرق بمعدل سنة واحدة كل 33 سنة ميلادية تقريباً.
                  </p>
                  <h4>هل تحفظ الحاسبة معلوماتي الشخصية؟</h4>
                  <p>
                    لا، جميع الحسابات تتم في متصفحك ولا يتم حفظ أي معلومات على
                    خوادمنا.
                  </p>
                  <h4>كيف أعرف يوم الأسبوع الذي ولدت فيه؟</h4>
                  <p>
                    بمجرد إدخال تاريخ ميلادك بالتقويم الميلادي، ستظهر لك الحاسبة يوم
                    الأسبوع الذي ولدت فيه.
                  </p>
                </div>
              </div>
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <Suspense fallback={<div>جاري التحميل...</div>}>
                <RelatedLinks />
              </Suspense>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <Suspense fallback={<div>جاري التحميل...</div>}>
            <MainCalculatorsList />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
export default AgeCalculatorGregorian;